import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Download, Plus, Search, Users } from 'lucide-react';
import { useEmployees } from '../api.js';
import { useAuthStore } from '../../../store/auth.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { Input } from '../../../shell/Field.js';
import { EmployeeFormModal } from '../components/EmployeeFormModal.js';
import { downloadCsv } from '../../../lib/csv.js';
import { formatDate } from '../../../lib/format.js';
import { PersonAvatar, employeeAvatarSrc } from '../../../shell/PersonAvatar.js';
import type { Employee } from '@shared/types';

const statusTone: Record<string, 'green' | 'yellow' | 'red'> = {
  Active: 'green',
  OnLeave: 'yellow',
  Terminated: 'red',
};

export function EmployeeListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading } = useEmployees({ search: debouncedSearch || undefined });

  const columns: Column<Employee>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Name',
      render: (e) => (
        <div className="flex items-center gap-2">
          <PersonAvatar
            name={e.name}
            src={employeeAvatarSrc(e.id, e.avatarStorageKey)}
            size="sm"
          />
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-100">{e.name}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">{e.email}</p>
          </div>
        </div>
      ),
    },
    { key: 'department', header: 'Department', render: (e) => e.department },
    { key: 'title', header: 'Title', render: (e) => e.title },
    {
      key: 'status',
      header: 'Status',
      render: (e) => <Badge tone={statusTone[e.employmentStatus]}>{e.employmentStatus}</Badge>,
    },
  ], []);

  const isAdmin = user?.role === 'Admin';
  const items = data?.items ?? [];

  const handleExport = () => {
    downloadCsv(
      'employees.csv',
      items.map((e) => ({
        Name: e.name,
        Email: e.email,
        Emails: (e.contacts ?? [])
          .filter((c) => c.kind === 'email')
          .map((c) => c.value)
          .join('; '),
        Phone: e.phone ?? '',
        Phones: (e.contacts ?? [])
          .filter((c) => c.kind === 'phone')
          .map((c) => c.value)
          .join('; '),
        Department: e.department,
        Title: e.title,
        Status: e.employmentStatus,
        'Hire Date': formatDate(e.hireDate),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Employees</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{items.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExport} disabled={items.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          {isAdmin && (
            <Button onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4" /> Add employee
            </Button>
          )}
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
        <Input
          aria-label="Search employees"
          placeholder="Search by name, email, department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table
          columns={columns}
          data={data.items}
          onRowClick={(e) => navigate(`/employees/${e.id}`)}
        />
      ) : (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No employees found"
          description={search ? 'Try a different search term.' : 'Add your first employee to get started.'}
          action={isAdmin && !search ? <Button onClick={() => setModalOpen(true)}><Plus className="h-4 w-4" /> Add employee</Button> : undefined}
        />
      )}

      <EmployeeFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
